import {
  getTotalActiveUsers,
  getAllUsers,
  getUserById,
  updateUser,
  updatePassword,
  getAssignedAdmin,
  updateManyUsers,
  getAssignedModerator,
  createNewUser,
  getCreatedUsersPerMonth,
  getLatestCreatedUsers,
  getMatchingUserError,
  listAllClients,
  listAllUsers,
} from '@/api/user/user';
import { TUserSchema } from '@/schemas/user/user.schema';
import { UserFilterType } from '@/entities/user';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notify } from '@/utils/toast.utils';
import { NOTIFY } from '@/utils/constant.utils';
import {
  LatestCreatedUser,
  ListAllClient,
  ListAllUser,
  TotalActiveUser,
} from '@/api/entities/user';
import { useAuth } from './auth.hooks';

export const useGetTotalActiveUsers = (query?: TotalActiveUser) => {
  return useQuery({
    queryKey: ['getTotalActiveUsers', query],
    queryFn: () => getTotalActiveUsers(query),
  });
};

export const useCreateUser = ({
  t,
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (user: TUserSchema) => {
      return createNewUser(user);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getLatestCreatedUsers'] });
      queryClient.invalidateQueries({ queryKey: ['getCreatedUsersPerMonth'] });
      queryClient.invalidateQueries({ queryKey: ['getAllUsers'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveUsers'] });
      queryClient.invalidateQueries({ queryKey: ['getMatchingUserError'] });

      notify(t('common:userCreatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (err: ErrorResponse) => {
      handleError?.(err);
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when creating user:', err.error);
    },
  });
};

export const useGetMatchingUserError = (query?: TUserSchema) => {
  return useQuery({
    queryKey: ['getMatchingUserError', query],
    queryFn: () => getMatchingUserError(query),
  });
};

export const useGetAllUsers = (
  filter: UserFilterType,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc',
) => {
  return useQuery({
    queryKey: ['getAllUsers', filter, page, sortBy, sortDirection],
    queryFn: () => getAllUsers(filter, page, limit, sortBy, sortDirection),
  });
};

export const useListAllUsers = (query?: ListAllUser) => {
  return useQuery({
    queryKey: ['listAllUsers', query],
    queryFn: () => listAllUsers(query),
  });
};

export const useListAllClients = (query?: ListAllClient) => {
  const { limitedRoleLoggedInUserId } = useAuth();

  return useQuery({
    queryKey: ['listAllClients', query, limitedRoleLoggedInUserId],
    queryFn: () =>
      listAllClients({ ...query, userId: limitedRoleLoggedInUserId }),
  });
};

export const useGetUserById = (id: string) => {
  return useQuery({
    queryKey: ['getUserById', id],
    queryFn: () => getUserById(id),
    enabled: !!id,
  });
};

export const useGetAssignedAdmin = (id: string) => {
  return useQuery({
    queryKey: ['getAssignedAdmin', id],
    queryFn: () => getAssignedAdmin(id),
    enabled: !!id,
  });
};

export const useGetAssignedModerator = (id: string) => {
  return useQuery({
    queryKey: ['getAssignedModerator', id],
    queryFn: () => getAssignedModerator(id),
    enabled: !!id,
  });
};

export const useGetCreatedUsersPerMonth = (userId?: string) => {
  return useQuery({
    queryKey: ['getCreatedUsersPerMonth', userId],
    queryFn: () => getCreatedUsersPerMonth(userId),
  });
};

export const useGetLatestCreatedUsers = (query?: LatestCreatedUser) => {
  return useQuery({
    queryKey: ['getLatestCreatedUsers', query],
    queryFn: () => getLatestCreatedUsers(query),
  });
};

export const useUpdateUser = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (user: TUserSchema) => updateUser(user),
    onSuccess: (_, variables) => {
      const { id } = variables;

      queryClient.invalidateQueries({ queryKey: ['getLatestCreatedUsers'] });
      queryClient.invalidateQueries({ queryKey: ['getCreatedUsersPerMonth'] });
      queryClient.invalidateQueries({ queryKey: ['getAllUsers'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveUsers'] });
      queryClient.invalidateQueries({ queryKey: ['getMatchingUserError'] });
      queryClient.invalidateQueries({ queryKey: ['getUserById', id] });

      notify(t('common:userUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (err: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating user:', err.error);
    },
  });
};

export const useUpdateManyUsers = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (users: TUserSchema[]) => updateManyUsers(users),
    onSuccess: (_, variables) => {
      for (const item of variables) {
        queryClient.invalidateQueries({ queryKey: ['getUserById', item?.id] });
      }

      queryClient.invalidateQueries({ queryKey: ['getLatestCreatedUsers'] });
      queryClient.invalidateQueries({ queryKey: ['getCreatedUsersPerMonth'] });
      queryClient.invalidateQueries({ queryKey: ['getAllUsers'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveUsers'] });
      queryClient.invalidateQueries({ queryKey: ['getMatchingUserError'] });

      notify(t('common:userUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating users:', error);
    },
  });
};

export const useUpdatePassword = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      updatePassword(id, password),
    onSuccess: () => {
      notify(t('common:passwordUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (err: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating user password:', err.error);
    },
  });
};
